/**
 * A feedback session is a wall-clock script of video manipulations
 * (seek, play/pause, rate, angles, pose, draws, voice) anchored to a clip range.
 * Visuals and actions are also stamped with the video frame where they happened
 * so replay can keep drawings aligned with the paused frame.
 */

export function newFeedbackId(prefix = "fb") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function normalizeFeedbackFps(fps) {
  const n = Number(fps);
  if (Number.isFinite(n) && n > 1 && n <= 240) return n;
  return 60;
}

export function frameFromVideoMs(videoMs, fps = 60) {
  const rate = normalizeFeedbackFps(fps);
  const ms = Math.max(0, Number(videoMs) || 0);
  return Math.max(0, Math.floor((ms / 1000) * rate + 1e-6));
}

export function videoMsFromFrame(frame, fps = 60) {
  const rate = normalizeFeedbackFps(fps);
  return Math.round((Math.max(0, Math.floor(Number(frame) || 0)) / rate) * 1000);
}

/** Same frame, or within half a frame of video time (legacy events without frame). */
export function sameFeedbackFrame(a, b, fps = 60) {
  if (a == null || b == null) return true;
  if (a.frame != null && b.frame != null) {
    return Number(a.frame) === Number(b.frame);
  }
  const aMs = a.videoMs != null ? Number(a.videoMs) : null;
  const bMs = b.videoMs != null ? Number(b.videoMs) : null;
  if (!Number.isFinite(aMs) || !Number.isFinite(bMs)) return true;
  const tol = Math.max(8, (1000 / normalizeFeedbackFps(fps)) * 0.55);
  return Math.abs(aMs - bMs) <= tol;
}

export function createFeedbackRecorder(anchorVideoMs, opts = {}) {
  const startedAt = Date.now();
  const events = [];
  const fps = normalizeFeedbackFps(opts.fps);
  let last = null;

  const stamp = (videoMs) => {
    const ms = Math.round(Math.max(0, Number(videoMs) || 0));
    return { videoMs: ms, frame: frameFromVideoMs(ms, fps) };
  };

  const push = (op, extra = {}) => {
    const t = Date.now() - startedAt;
    const prev = events[events.length - 1];
    if (
      prev &&
      prev.op === op &&
      prev.t === t &&
      JSON.stringify({ ...prev, t: 0, op: "" }) ===
        JSON.stringify({ ...extra, t: 0, op: "" })
    ) {
      return;
    }
    events.push({ t, op, ...extra });
  };

  const observe = (snapshot) => {
    const videoMs = Math.round(Math.max(0, Number(snapshot.videoMs) || 0));
    const frame =
      snapshot.frame != null && Number.isFinite(Number(snapshot.frame))
        ? Math.max(0, Math.floor(Number(snapshot.frame)))
        : frameFromVideoMs(videoMs, fps);
    const paused = Boolean(snapshot.paused);
    const rate = Number(snapshot.rate) > 0 ? Number(snapshot.rate) : 1;
    const angles =
      snapshot.angles === "knees" || snapshot.angles === "elbows"
        ? snapshot.angles
        : "off";
    const anglesPlayerRaw = String(snapshot.anglesPlayer ?? snapshot.player ?? "A")
      .trim()
      .toUpperCase();
    const anglesPlayer =
      anglesPlayerRaw === "B" ||
      anglesPlayerRaw === "RIGHT" ||
      anglesPlayerRaw === "D"
        ? "B"
        : "A";
    const pose = Boolean(snapshot.pose);
    const zoomScale = Math.max(1, Number(snapshot.zoomScale) || 1);
    const panX = Number(snapshot.panX) || 0;
    const panY = Number(snapshot.panY) || 0;

    if (!last) {
      push("seek", { videoMs, frame });
      push(paused ? "pause" : "play", { videoMs, frame });
      push("rate", { rate, videoMs, frame });
      push("angles", { mode: angles, player: anglesPlayer, videoMs, frame });
      push("pose", { on: pose, videoMs, frame });
      push("zoom", { zoomScale, panX, panY, videoMs, frame });
      last = {
        videoMs,
        frame,
        paused,
        rate,
        angles,
        anglesPlayer,
        pose,
        zoomScale,
        panX,
        panY,
      };
      return;
    }

    if (frame !== last.frame || Math.abs(videoMs - last.videoMs) > 1) {
      push("seek", { videoMs, frame });
    }
    if (paused !== last.paused) {
      push(paused ? "pause" : "play", { videoMs, frame });
    }
    if (Math.abs(rate - last.rate) > 0.01) {
      push("rate", { rate, videoMs, frame });
    }
    if (angles !== last.angles || anglesPlayer !== last.anglesPlayer) {
      push("angles", { mode: angles, player: anglesPlayer, videoMs, frame });
    }
    if (pose !== last.pose) {
      push("pose", { on: pose, videoMs, frame });
    }
    if (
      Math.abs(zoomScale - last.zoomScale) > 0.02 ||
      Math.abs(panX - last.panX) > 4 ||
      Math.abs(panY - last.panY) > 4
    ) {
      push("zoom", { zoomScale, panX, panY, videoMs, frame });
    }
    last = {
      videoMs,
      frame,
      paused,
      rate,
      angles,
      anglesPlayer,
      pose,
      zoomScale,
      panX,
      panY,
    };
  };

  const timedPoints = (points, { atStart = false, frame, videoMs } = {}) =>
    (points || []).map((point) => ({
      x: point.x,
      y: point.y,
      at: atStart ? 0 : Math.max(0, Number(point.at) - startedAt || 0),
      ...(frame != null ? { frame } : {}),
      ...(videoMs != null ? { videoMs } : {}),
    }));

  const resolveMediaStamp = (opts = {}) => {
    if (opts.videoMs != null || opts.frame != null) {
      const videoMs =
        opts.videoMs != null
          ? Math.round(Math.max(0, Number(opts.videoMs) || 0))
          : videoMsFromFrame(opts.frame, fps);
      const frame =
        opts.frame != null && Number.isFinite(Number(opts.frame))
          ? Math.max(0, Math.floor(Number(opts.frame)))
          : frameFromVideoMs(videoMs, fps);
      return { videoMs, frame };
    }
    if (last) return { videoMs: last.videoMs, frame: last.frame };
    return stamp(anchorVideoMs);
  };

  const seedStrokes = (strokes) => {
    const { videoMs, frame } = resolveMediaStamp();
    for (const stroke of strokes || []) {
      if (!stroke?.points?.length) continue;
      events.push({
        t: 0,
        op: "stroke",
        videoMs,
        frame,
        drawMs: 0,
        seeded: true,
        stroke: {
          color: stroke.color,
          width: stroke.width,
          erase: Boolean(stroke.erase),
          ...(stroke.coordSpace === "frame" ? { coordSpace: "frame" } : {}),
          ...(Number(stroke.widthNorm) > 0 ? { widthNorm: Number(stroke.widthNorm) } : {}),
          points: timedPoints(stroke.points, { atStart: true, frame, videoMs }),
        },
      });
    }
  };

  const addStroke = (stroke, opts = {}) => {
    if (!stroke?.points?.length) return;
    const { videoMs, frame } = resolveMediaStamp(opts);
    const points = timedPoints(stroke.points, { frame, videoMs });
    const start = points.reduce((min, point) => Math.min(min, point.at), points[0].at);
    const end = points.reduce((max, point) => Math.max(max, point.at), start);
    events.push({
      t: start,
      op: "stroke",
      videoMs,
      frame,
      drawMs: Math.max(0, end - start),
      stroke: {
        color: stroke.color,
        width: stroke.width,
        erase: Boolean(stroke.erase),
        ...(stroke.coordSpace === "frame" ? { coordSpace: "frame" } : {}),
        ...(Number(stroke.widthNorm) > 0 ? { widthNorm: Number(stroke.widthNorm) } : {}),
        points,
      },
    });
    last = { ...(last || {}), videoMs, frame };
  };

  const addVoice = (voiceDataUrl, opts = {}) => {
    if (!voiceDataUrl) return;
    const atMs =
      opts.atMs != null ? Math.max(0, Math.round(Number(opts.atMs) || 0)) : Date.now() - startedAt;
    const durationMs =
      opts.durationMs != null ? Math.max(0, Math.round(Number(opts.durationMs) || 0)) : null;
    const { videoMs, frame } = resolveMediaStamp(opts);
    events.push({
      t: atMs,
      op: "voice",
      videoMs,
      frame,
      voiceDataUrl,
      durationMs,
    });
  };

  const addScreen = (screenDataUrl, opts = {}) => {
    if (!screenDataUrl) return;
    const atMs =
      opts.atMs != null ? Math.max(0, Math.round(Number(opts.atMs) || 0)) : Date.now() - startedAt;
    const durationMs =
      opts.durationMs != null ? Math.max(0, Math.round(Number(opts.durationMs) || 0)) : null;
    const { videoMs, frame } = resolveMediaStamp(opts);
    events.push({
      t: atMs,
      op: "screen",
      videoMs,
      frame,
      screenDataUrl,
      durationMs,
    });
  };

  const finish = () => {
    const wallMs = Math.max(0, Date.now() - startedAt);
    const mediaEnds = events
      .filter((e) => e.op === "voice" || e.op === "screen")
      .map((e) => (Number(e.t) || 0) + Math.max(0, Number(e.durationMs) || 0));
    const durationMs = Math.max(wallMs, ...mediaEnds, 0);
    const videoTimes = events
      .map((e) => Number(e.videoMs))
      .filter((n) => Number.isFinite(n));
    const anchor = Math.round(Math.max(0, Number(anchorVideoMs) || videoTimes[0] || 0));
    if (!videoTimes.length) videoTimes.push(anchor);
    return {
      id: newFeedbackId(),
      anchorMs: anchor,
      anchorFrame: frameFromVideoMs(anchor, fps),
      fps,
      rangeStartMs: Math.min(...videoTimes, anchor),
      rangeEndMs: Math.max(...videoTimes, anchor),
      durationMs,
      createdAt: new Date().toISOString(),
      events: events.slice().sort((a, b) => a.t - b.t),
    };
  };

  return { observe, seedStrokes, addStroke, addVoice, addScreen, finish, events, fps };
}

export function sessionHasContent(session) {
  const events = session?.events || [];
  return events.some(
    (e) =>
      e.op === "stroke" ||
      e.op === "voice" ||
      e.op === "screen" ||
      (Number(e.t) > 200 &&
        (e.op === "seek" ||
          e.op === "rate" ||
          e.op === "angles" ||
          e.op === "pose" ||
          e.op === "zoom" ||
          e.op === "pause" ||
          e.op === "play"))
  );
}

/** Events whose wall time is <= elapsed, in order. */
export function eventsDue(session, elapsedMs, appliedCount) {
  const events = session?.events || [];
  const start = Math.max(0, appliedCount || 0);
  const out = [];
  for (let i = start; i < events.length; i += 1) {
    if (Number(events[i].t) > elapsedMs) break;
    out.push(events[i]);
  }
  return out;
}

/**
 * Strokes due by wall-clock, optionally filtered to the video frame currently shown.
 * Pass videoMs/frame during replay so drawings stay on the frame where they were made.
 */
export function strokesVisibleAt(session, elapsedMs, opts = {}) {
  const fps = normalizeFeedbackFps(opts.fps || session?.fps);
  const filterByFrame = opts.frame != null || opts.videoMs != null;
  const view = {
    frame: opts.frame != null ? Math.floor(Number(opts.frame)) : null,
    videoMs: opts.videoMs != null ? Number(opts.videoMs) : null,
  };
  const strokes = [];
  for (const event of session?.events || []) {
    if (event.op !== "stroke" || !event.stroke) continue;
    const start = Number(event.t) || 0;
    if (start > elapsedMs) continue;
    if (
      filterByFrame &&
      !sameFeedbackFrame(
        { frame: event.frame, videoMs: event.videoMs },
        view,
        fps
      )
    ) {
      continue;
    }
    const points = event.stroke.points || [];
    const drawMs = Number(event.drawMs) || 0;
    if (!points.length) continue;
    if (points.some((point) => Number.isFinite(point.at))) {
      const visible = points.filter((point) => (Number(point.at) || 0) <= elapsedMs);
      if (visible.length) strokes.push({ ...event.stroke, points: visible });
      continue;
    }
    if (!drawMs || elapsedMs >= start + drawMs) {
      strokes.push(event.stroke);
      continue;
    }
    const count = Math.max(1, Math.ceil(points.length * ((elapsedMs - start) / drawMs)));
    strokes.push({ ...event.stroke, points: points.slice(0, count) });
  }
  return strokes;
}

function sameStrokeStart(a, b) {
  const pa = a?.points?.[0];
  const pb = b?.points?.[0];
  if (!pa || !pb) return false;
  return Math.abs(pa.x - pb.x) < 0.002 && Math.abs(pa.y - pb.y) < 0.002;
}

/** Frame notes drawn or voiced as part of a saved feedback. */
export function noteBelongsToSession(note, session) {
  if (!note || !session) return false;
  if (note.sessionId && note.sessionId === session.id) return true;

  const noteAt = Date.parse(note.createdAt || "");
  const sessionAt = Date.parse(session.createdAt || "");
  const durationMs = Number(session.durationMs) || 0;
  if (Number.isFinite(noteAt) && Number.isFinite(sessionAt)) {
    if (noteAt >= sessionAt - 2000 && noteAt <= sessionAt + durationMs + 2000) {
      return true;
    }
  }

  const times = [session.rangeStartMs, session.rangeEndMs, session.anchorMs];
  for (const event of session.events || []) {
    if (event?.videoMs != null) times.push(event.videoMs);
  }
  const t = Number(note.tMs) || 0;
  if (times.some((ms) => Number.isFinite(Number(ms)) && Math.abs(t - Number(ms)) <= 2000)) {
    return true;
  }

  const sessionStrokes = (session.events || [])
    .filter((event) => event?.op === "stroke" && event.stroke)
    .map((event) => event.stroke);
  return (note.strokes || []).some((stroke) =>
    sessionStrokes.some((other) => sameStrokeStart(stroke, other))
  );
}

export function sessionReplayDurationMs(session) {
  const base = Math.max(0, Number(session?.durationMs) || 0);
  let mediaEnd = 0;
  for (const event of session?.events || []) {
    if (event?.op !== "voice" && event?.op !== "screen") continue;
    const start = Math.max(0, Number(event.t) || 0);
    const dur = Math.max(0, Number(event.durationMs) || 0);
    // Legacy takes stamped media at the end with no duration — keep review open a bit.
    mediaEnd = Math.max(mediaEnd, start + (dur || (start > 0 ? 8000 : 0)));
  }
  return Math.max(base, mediaEnd);
}

/**
 * Normalize legacy sessions that stamped voice/screen at stop-time (near the end)
 * so review plays media from the start of the take.
 */
export function normalizeSessionForReplay(session) {
  if (!session || !Array.isArray(session.events)) return session;
  const durationMs = Math.max(0, Number(session.durationMs) || 0);
  let changed = false;
  const events = session.events.map((event) => {
    if (event?.op !== "voice" && event?.op !== "screen") return event;
    const hasMedia = Boolean(event.voiceDataUrl || event.screenDataUrl);
    if (!hasMedia) return event;
    const t = Math.max(0, Number(event.t) || 0);
    const dur = Math.max(0, Number(event.durationMs) || 0);
    // Old recorder used Date.now()-startedAt at FileReader completion ≈ end of take.
    if (dur > 0 && t <= 250) return event;
    if (durationMs > 0 && t >= Math.max(0, durationMs - 1200)) {
      changed = true;
      return {
        ...event,
        t: 0,
        durationMs: dur > 0 ? dur : Math.max(1500, durationMs),
      };
    }
    if (!dur && t > 500) {
      changed = true;
      return {
        ...event,
        t: 0,
        durationMs: Math.max(1500, durationMs || t),
      };
    }
    return event;
  });
  if (!changed) return session;
  return {
    ...session,
    durationMs: sessionReplayDurationMs({ ...session, events }),
    events,
  };
}

export function formatFeedbackClock(ms) {
  const total = Math.max(0, Math.round(Number(ms) || 0));
  const sec = Math.floor(total / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Video time the feedback is pinned to. */
export function feedbackAnchorMs(session) {
  const anchor = Number(session?.anchorMs);
  if (Number.isFinite(anchor) && anchor >= 0) return anchor;
  const start = Number(session?.rangeStartMs);
  if (Number.isFinite(start) && start >= 0) return start;
  return 0;
}

/** How far past the anchor a coarse timeupdate can still count as arrival. */
const AUTO_FEEDBACK_GRACE_MS = 1200;

/**
 * Drop consumed flags once playback is clearly before that feedback again,
 * or the user seeks backward onto its frame. Staying on the frame after a
 * take finishes must not clear it — that is what looped the same feedback.
 */
export function releaseConsumedFeedback(sessions, tMs, prevMs, consumed) {
  if (!(consumed instanceof Set) || !consumed.size) return;
  const t = Math.max(0, Math.round(Number(tMs) || 0));
  const prev = Math.max(0, Math.round(Number(prevMs) || 0));
  const jumpedBack = prev - t > 400;
  for (const session of sessions || []) {
    if (!session?.id || !consumed.has(session.id)) continue;
    const anchor = feedbackAnchorMs(session);
    if (t < anchor - 700) {
      consumed.delete(session.id);
      continue;
    }
    if (jumpedBack && prev > anchor + 400 && t <= anchor + 200) {
      consumed.delete(session.id);
    }
  }
}

/**
 * Feedback to auto-play when playback arrives at its anchor frame.
 * Consumed ids are skipped so the take cannot restart while the playhead
 * is still on that frame.
 */
export function pickAutoFeedbackSession(sessions, tMs, prevMs, consumed) {
  if (!Array.isArray(sessions) || !sessions.length) return null;
  const t = Math.max(0, Math.round(Number(tMs) || 0));
  const prev = Math.max(0, Math.round(Number(prevMs) || 0));
  const played = consumed instanceof Set ? consumed : new Set();
  let best = null;
  for (const session of sessions) {
    if (!session?.id || played.has(session.id)) continue;
    const anchor = feedbackAnchorMs(session);
    const crossed =
      prev < anchor && t >= anchor && t - anchor <= AUTO_FEEDBACK_GRACE_MS;
    const seeked = Math.abs(t - prev) > 500;
    const landed =
      seeked && t >= anchor && t - anchor <= AUTO_FEEDBACK_GRACE_MS && prev < anchor;
    const resting = Math.abs(t - anchor) <= 160 && Math.abs(t - prev) <= 160;
    if (!crossed && !landed && !resting) continue;
    if (!best || anchor < feedbackAnchorMs(best)) best = session;
  }
  return best;
}

/** Closest feedback whose frame is near the playhead, if any. */
export function feedbackAtPlayhead(sessions, tMs, windowMs = 1500) {
  const t = Math.max(0, Math.round(Number(tMs) || 0));
  const window = Math.max(160, Number(windowMs) || 1500);
  let best = null;
  let bestDist = Infinity;
  for (const session of sessions || []) {
    if (!session?.id) continue;
    const dist = Math.abs(feedbackAnchorMs(session) - t);
    if (dist <= window && dist < bestDist) {
      best = session;
      bestDist = dist;
    }
  }
  return best;
}
